import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, federation } from '../../utils/api';
import { useAuth } from '../../utils/AuthContext';
import {
  orgCanAccessFederation,
  orgCanAccessPostMatch,
} from '../../utils/productWorkspace';
import FederationGate from './FederationGate';
import {
  ErrorBanner,
  LoadingBlock,
  PageHeader,
  btnPrimary,
  btnSecondary,
  canWrite,
  inputClass,
  isOwner,
  isViewer,
  safeText,
} from './federationShared';

const DEFAULT_TEMPLATE_JSON = {
  fields: [
    { key: 'summary', label: 'Program summary', required: true, type: 'textarea' },
    { key: 'evidence', label: 'Evidence references', required: false, type: 'textarea' },
    { key: 'priorities', label: 'Development priorities', required: true, type: 'textarea' },
  ],
  evidenceExpectations: 'Describe submitted program evidence and what remains outstanding for leadership review.',
};

function SettingsInner() {
  const { user, org } = useAuth();
  const owner = isOwner(user);
  const viewer = isViewer(user);
  const writable = canWrite(user);

  const [periods, setPeriods] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [users, setUsers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [auditEvents, setAuditEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [periodForm, setPeriodForm] = useState({
    name: '',
    startDate: '',
    endDate: '',
    status: 'Open',
  });
  const [savingPeriod, setSavingPeriod] = useState(false);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    evidenceExpectations: DEFAULT_TEMPLATE_JSON.evidenceExpectations,
    fieldsText: DEFAULT_TEMPLATE_JSON.fields.map((f) => `${f.label}${f.required ? '*' : ''}`).join('\n'),
  });
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState('');
  const [editingSourceTemplateId, setEditingSourceTemplateId] = useState('');
  const [previewTemplateId, setPreviewTemplateId] = useState('');
  const [accessForm, setAccessForm] = useState({ userId: '', programId: '', accessLevel: 'staff' });
  const [savingAccess, setSavingAccess] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    const tasks = [
      federation.getPeriods(),
      federation.getTemplates(),
      federation.getPrograms().catch(() => ({ programs: [] })),
    ];
    if (owner) {
      tasks.push(api.getUsers().catch(() => ({ users: [] })));
      tasks.push(federation.getProgramAccess().catch(() => ({ assignments: [] })));
      tasks.push(federation.getAudit({ limit: 25 }).catch(() => ({ events: [] })));
    }
    Promise.all(tasks)
      .then((results) => {
        const [p, t, prog, u, a, audit] = results;
        setPeriods(p.periods || []);
        setTemplates(t.templates || []);
        setPrograms(prog.programs || []);
        if (owner) {
          setUsers((u?.users || []).filter((row) => String(row.role || '').toLowerCase() !== 'owner'));
          setAssignments(a?.assignments || []);
          setAuditEvents(audit?.events || []);
        }
      })
      .catch((e) => {
        setPeriods([]);
        setTemplates([]);
        setError(e.message || 'Could not load Federation settings');
      })
      .finally(() => setLoading(false));
  }, [owner]);

  useEffect(() => {
    load();
  }, [load]);

  const createPeriod = async (e) => {
    e.preventDefault();
    if (!writable) return;
    if (!periodForm.name.trim() || !periodForm.startDate || !periodForm.endDate) {
      setError('Period name, start date, and end date are required');
      return;
    }
    setSavingPeriod(true);
    setError('');
    try {
      await federation.createPeriod({
        name: periodForm.name.trim(),
        startDate: periodForm.startDate,
        endDate: periodForm.endDate,
        status: periodForm.status,
      });
      setPeriodForm({ name: '', startDate: '', endDate: '', status: 'Open' });
      load();
    } catch (err) {
      setError(err.message || 'Could not create reporting period');
    } finally {
      setSavingPeriod(false);
    }
  };

  const updatePeriodStatus = async (period, status) => {
    if (!writable) return;
    setError('');
    try {
      await federation.updatePeriod({ id: period.id, status });
      load();
    } catch (err) {
      setError(err.message || 'Could not update period');
    }
  };

  const deletePeriod = async (period) => {
    if (!owner) {
      setError('Only organization owners can delete reporting periods');
      return;
    }
    setError('');
    try {
      await federation.deletePeriod(period.id);
      load();
    } catch (err) {
      setError(err.message || 'Could not delete period');
    }
  };

  const parseTemplateFields = (fieldsText) => {
    const lines = String(fieldsText || '')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
    if (lines.length === 0) return DEFAULT_TEMPLATE_JSON.fields;
    return lines.map((line, index) => {
      const required = line.endsWith('*');
      const label = required ? line.slice(0, -1).trim() : line;
      const key = label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') || `field_${index + 1}`;
      return { key, label, required, type: 'textarea' };
    });
  };

  const createTemplate = async (e) => {
    e.preventDefault();
    if (!writable) return;
    if (!templateForm.name.trim()) {
      setError('Template name is required');
      return;
    }
    const fields = parseTemplateFields(templateForm.fieldsText);
    if (!fields.length) {
      setError('Add at least one template field before saving');
      return;
    }
    setSavingTemplate(true);
    setError('');
    try {
      const templateJson = {
        fields,
        evidenceExpectations: templateForm.evidenceExpectations.trim(),
      };
      if (editingTemplateId) {
        await federation.updateTemplate({
          id: editingTemplateId,
          name: templateForm.name.trim(),
          templateJson,
        });
      } else if (editingSourceTemplateId) {
        await federation.createTemplate({
          name: templateForm.name.trim(),
          templateJson,
          sourceTemplateId: editingSourceTemplateId,
        });
      } else {
        await federation.createTemplate({
          name: templateForm.name.trim(),
          templateJson,
        });
      }
      setTemplateForm({
        name: '',
        evidenceExpectations: DEFAULT_TEMPLATE_JSON.evidenceExpectations,
        fieldsText: DEFAULT_TEMPLATE_JSON.fields.map((f) => `${f.label}${f.required ? '*' : ''}`).join('\n'),
      });
      setEditingTemplateId('');
      setEditingSourceTemplateId('');
      load();
    } catch (err) {
      setError(err.message || 'Could not save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const beginEditTemplate = (template) => {
    const json = template.templateJson || {};
    const fields = Array.isArray(json.fields) ? json.fields : DEFAULT_TEMPLATE_JSON.fields;
    const isImmutable = template.status === 'Active' || template.status === 'Archived';
    setEditingTemplateId(isImmutable ? '' : template.id);
    setEditingSourceTemplateId(isImmutable ? template.id : '');
    setTemplateForm({
      name: template.name || '',
      evidenceExpectations: json.evidenceExpectations || '',
      fieldsText: fields.map((f) => `${f.label || f.key}${f.required ? '*' : ''}`).join('\n'),
    });
  };

  const activateTemplate = async (template) => {
    if (!owner) {
      setError('Only organization owners can activate templates');
      return;
    }
    const fields = Array.isArray(template.templateJson?.fields) ? template.templateJson.fields : [];
    if (!fields.length) {
      setError('Activate requires at least one configured field. Edit the draft first.');
      return;
    }
    setError('');
    try {
      await federation.updateTemplate({ id: template.id, status: 'Active' });
      setPreviewTemplateId('');
      load();
    } catch (err) {
      setError(err.message || 'Could not activate template');
    }
  };

  const assignAccess = async (e) => {
    e.preventDefault();
    if (!owner) return;
    if (!accessForm.userId || !accessForm.programId) {
      setError('Select a user and program');
      return;
    }
    setSavingAccess(true);
    setError('');
    try {
      await federation.assignProgramAccess(accessForm);
      setAccessForm({ userId: '', programId: '', accessLevel: 'staff' });
      load();
    } catch (err) {
      setError(err.message || 'Could not assign program access');
    } finally {
      setSavingAccess(false);
    }
  };

  const revokeAccess = async (assignment) => {
    if (!owner) return;
    setError('');
    try {
      await federation.revokeProgramAccess(assignment.id);
      load();
    } catch (err) {
      setError(err.message || 'Could not revoke program access');
    }
  };

  const federationEnabled = orgCanAccessFederation(org);
  const postMatchEnabled = orgCanAccessPostMatch(org);

  return (
    <>
      <PageHeader
        title="Federation settings"
        description="Product entitlement, reporting periods, templates, program access, and audit history."
      />

      <ErrorBanner message={error} onRetry={load} />

      <section className="border border-navy-200 rounded-xl bg-white p-5 mb-6">
        <h2 className="text-base font-semibold text-navy-900 mb-3">Product entitlement</h2>
        <dl className="space-y-2 text-sm">
          <div className="flex justify-between gap-4">
            <dt className="text-gray-500">Federation</dt>
            <dd className="font-medium text-navy-900">
              {federationEnabled ? 'Enabled' : 'Not enabled'}
            </dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-gray-500">Post-Match</dt>
            <dd className="font-medium text-navy-900">
              {postMatchEnabled ? 'Enabled' : 'Not enabled'}
            </dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-gray-500">Contract status</dt>
            <dd className="font-medium text-navy-900">
              {safeText(org?.federationContractStatus, '—')}
            </dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-gray-500">Contract ends</dt>
            <dd className="font-medium text-navy-900">
              {safeText(org?.federationContractEndsAt, '—')}
            </dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-gray-500">Participating team allowance</dt>
            <dd className="font-medium text-navy-900">
              {org?.federationMaxTeams === null || org?.federationMaxTeams === undefined
                ? 'Contract-defined (no software cap set)'
                : String(org.federationMaxTeams)}
            </dd>
          </div>
        </dl>
        <p className="text-sm text-gray-500 mt-4">
          Organization profile, reporting timezone, and billing are managed in{' '}
          <Link
            to="/settings"
            className="text-navy-900 font-medium underline focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-400 rounded"
          >
            Settings
          </Link>
          .
          {!postMatchEnabled && (
            <span className="block mt-2">
              This organization is Federation-only. Post-Match plan controls are not required for participating-team management.
            </span>
          )}
        </p>
      </section>

      {loading && <LoadingBlock label="Loading settings…" />}

      {!loading && (
        <>
          <section className="mb-8">
            <h2 className="text-lg font-semibold text-navy-900 mb-3">Reporting periods</h2>

            {writable && !viewer && (
              <form
                onSubmit={createPeriod}
                className="border border-navy-200 rounded-xl bg-white p-5 mb-4 space-y-3 max-w-xl"
              >
                <h3 className="text-sm font-semibold text-navy-900">Add period</h3>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Name</label>
                  <input
                    className={inputClass}
                    value={periodForm.name}
                    onChange={(e) => setPeriodForm((f) => ({ ...f, name: e.target.value }))}
                    maxLength={120}
                    required
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Start</label>
                    <input
                      type="date"
                      className={inputClass}
                      value={periodForm.startDate}
                      onChange={(e) => setPeriodForm((f) => ({ ...f, startDate: e.target.value }))}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">End</label>
                    <input
                      type="date"
                      className={inputClass}
                      value={periodForm.endDate}
                      onChange={(e) => setPeriodForm((f) => ({ ...f, endDate: e.target.value }))}
                      required
                    />
                  </div>
                </div>
                <button type="submit" disabled={savingPeriod} className={btnPrimary}>
                  {savingPeriod ? 'Saving…' : 'Create period'}
                </button>
              </form>
            )}

            {periods.length === 0 ? (
              <p className="text-sm text-gray-500 border border-navy-200 rounded-xl bg-white px-4 py-6">
                No reporting periods yet. Create one before generating quarterly exports.
              </p>
            ) : (
              <ul className="border border-navy-200 rounded-xl bg-white divide-y divide-navy-100">
                {periods.map((period) => (
                  <li key={period.id} className="px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <p className="font-medium text-navy-900">{safeText(period.name)}</p>
                      <p className="text-sm text-gray-500">
                        {safeText(period.startDate)} – {safeText(period.endDate)} · {safeText(period.status)}
                      </p>
                    </div>
                    {writable && (
                      <div className="flex flex-wrap gap-2">
                        {period.status === 'Open' ? (
                          <button
                            type="button"
                            className={btnSecondary}
                            onClick={() => updatePeriodStatus(period, 'Closed')}
                          >
                            Close
                          </button>
                        ) : (
                          <button
                            type="button"
                            className={btnSecondary}
                            onClick={() => updatePeriodStatus(period, 'Open')}
                          >
                            Reopen
                          </button>
                        )}
                        {owner && (
                          <button
                            type="button"
                            className={btnSecondary}
                            onClick={() => deletePeriod(period)}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="mb-8">
            <h2 className="text-lg font-semibold text-navy-900 mb-3">Reporting templates</h2>

            {writable && !viewer && (
              <form
                onSubmit={createTemplate}
                className="border border-navy-200 rounded-xl bg-white p-5 mb-4 space-y-3 max-w-xl"
              >
                <h3 className="text-sm font-semibold text-navy-900">
                  {editingTemplateId
                    ? 'Edit draft template'
                    : editingSourceTemplateId
                      ? 'Create new draft version'
                      : 'Create draft template'}
                </h3>
                {editingSourceTemplateId && (
                  <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                    Active and archived templates are immutable. Saving creates a new draft version you can preview and activate.
                  </p>
                )}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Template name</label>
                  <input
                    className={inputClass}
                    value={templateForm.name}
                    onChange={(e) => setTemplateForm((f) => ({ ...f, name: e.target.value }))}
                    maxLength={120}
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Fields (one per line; append * for required)
                  </label>
                  <textarea
                    className={inputClass}
                    rows={5}
                    value={templateForm.fieldsText}
                    onChange={(e) => setTemplateForm((f) => ({ ...f, fieldsText: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Evidence expectations</label>
                  <textarea
                    className={inputClass}
                    rows={3}
                    value={templateForm.evidenceExpectations}
                    onChange={(e) => setTemplateForm((f) => ({ ...f, evidenceExpectations: e.target.value }))}
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  <button type="submit" disabled={savingTemplate} className={btnPrimary}>
                    {savingTemplate
                      ? 'Saving…'
                      : editingTemplateId
                        ? 'Save draft'
                        : editingSourceTemplateId
                          ? 'Create new version'
                          : 'Create draft'}
                  </button>
                  {(editingTemplateId || editingSourceTemplateId) && (
                    <button
                      type="button"
                      className={btnSecondary}
                      onClick={() => {
                        setEditingTemplateId('');
                        setEditingSourceTemplateId('');
                        setTemplateForm({
                          name: '',
                          evidenceExpectations: DEFAULT_TEMPLATE_JSON.evidenceExpectations,
                          fieldsText: DEFAULT_TEMPLATE_JSON.fields.map((f) => `${f.label}${f.required ? '*' : ''}`).join('\n'),
                        });
                      }}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </form>
            )}

            {templates.length === 0 ? (
              <p className="text-sm text-gray-500 border border-navy-200 rounded-xl bg-white px-4 py-6">
                No templates yet. Create a draft with required fields and evidence expectations, then an Owner can activate it.
              </p>
            ) : (
              <ul className="border border-navy-200 rounded-xl bg-white divide-y divide-navy-100">
                {templates.map((template) => {
                  const fields = Array.isArray(template.templateJson?.fields)
                    ? template.templateJson.fields
                    : [];
                  const immutable = template.status === 'Active' || template.status === 'Archived';
                  return (
                    <li key={template.id} className="px-4 py-3 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      <div>
                        <p className="font-medium text-navy-900">{safeText(template.name)}</p>
                        <p className="text-sm text-gray-500">
                          v{safeText(template.version, '1')} · {safeText(template.status)}
                          {template.activatedAt ? ` · Activated ${safeText(template.activatedAt)}` : ''}
                        </p>
                        {fields.length > 0 && (
                          <p className="text-xs text-gray-500 mt-1">
                            Fields: {fields.map((f) => `${f.label || f.key}${f.required ? '*' : ''}`).join(', ')}
                          </p>
                        )}
                        {template.templateJson?.evidenceExpectations && (
                          <p className="text-xs text-gray-500 mt-1">
                            Evidence: {safeText(template.templateJson.evidenceExpectations)}
                          </p>
                        )}
                        {previewTemplateId === template.id && (
                          <div className="mt-3 rounded-lg border border-navy-100 bg-slate-50 p-3 text-sm text-gray-700 space-y-2">
                            <p className="font-medium text-navy-900">Draft preview</p>
                            {fields.map((f) => (
                              <div key={f.key}>
                                <p className="text-xs font-medium text-gray-500">{safeText(f.label || f.key)}{f.required ? ' *' : ''}</p>
                                <div className="mt-1 h-8 rounded border border-dashed border-navy-200 bg-white" />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {writable && (
                          <button
                            type="button"
                            className={btnSecondary}
                            onClick={() => beginEditTemplate(template)}
                          >
                            {immutable ? 'New version' : 'Edit draft'}
                          </button>
                        )}
                        <button
                          type="button"
                          className={btnSecondary}
                          onClick={() => setPreviewTemplateId((id) => (id === template.id ? '' : template.id))}
                        >
                          {previewTemplateId === template.id ? 'Hide preview' : 'Preview'}
                        </button>
                        {owner && template.status === 'Draft' && (
                          <button
                            type="button"
                            className={btnPrimary}
                            onClick={() => activateTemplate(template)}
                          >
                            Activate
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          {owner && (
            <section className="mb-8">
              <h2 className="text-lg font-semibold text-navy-900 mb-3">Staff and program access</h2>
              <p className="text-sm text-gray-500 mb-4">
                Owners see every program. Assign Staff and Viewer users to specific programs here.
              </p>
              <form
                onSubmit={assignAccess}
                className="border border-navy-200 rounded-xl bg-white p-5 mb-4 grid grid-cols-1 sm:grid-cols-4 gap-3 max-w-4xl"
              >
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">User</label>
                  <select
                    className={inputClass}
                    value={accessForm.userId}
                    onChange={(e) => setAccessForm((f) => ({ ...f, userId: e.target.value }))}
                    required
                  >
                    <option value="">Select user</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {safeText(u.name || u.email)} ({safeText(u.role)})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Program</label>
                  <select
                    className={inputClass}
                    value={accessForm.programId}
                    onChange={(e) => setAccessForm((f) => ({ ...f, programId: e.target.value }))}
                    required
                  >
                    <option value="">Select program</option>
                    {programs.map((p) => (
                      <option key={p.id} value={p.id}>{safeText(p.name)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Access</label>
                  <select
                    className={inputClass}
                    value={accessForm.accessLevel}
                    onChange={(e) => setAccessForm((f) => ({ ...f, accessLevel: e.target.value }))}
                  >
                    <option value="staff">staff</option>
                    <option value="viewer">viewer</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <button type="submit" disabled={savingAccess} className={btnPrimary}>
                    {savingAccess ? 'Saving…' : 'Assign access'}
                  </button>
                </div>
              </form>
              {assignments.length === 0 ? (
                <p className="text-sm text-gray-500 border border-navy-200 rounded-xl bg-white px-4 py-6">
                  No program-scoped assignments yet.
                </p>
              ) : (
                <ul className="border border-navy-200 rounded-xl bg-white divide-y divide-navy-100">
                  {assignments.map((assignment) => (
                    <li key={assignment.id} className="px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div>
                        <p className="font-medium text-navy-900">
                          {safeText(assignment.userName || assignment.userEmail)} → {safeText(assignment.programName)}
                        </p>
                        <p className="text-sm text-gray-500">{safeText(assignment.accessLevel)}</p>
                      </div>
                      <button
                        type="button"
                        className={btnSecondary}
                        onClick={() => revokeAccess(assignment)}
                      >
                        Revoke
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          {owner && (
            <section>
              <h2 className="text-lg font-semibold text-navy-900 mb-3">Organization audit trail</h2>
              {auditEvents.length === 0 ? (
                <p className="text-sm text-gray-500 border border-navy-200 rounded-xl bg-white px-4 py-6">
                  No audited Federation events yet.
                </p>
              ) : (
                <ul className="border border-navy-200 rounded-xl bg-white divide-y divide-navy-100">
                  {auditEvents.map((event) => (
                    <li key={event.id} className="px-4 py-3">
                      <p className="text-sm font-medium text-navy-900">
                        {safeText(event.action)} · {safeText(event.resourceType)}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {safeText(event.createdAt)}
                        {event.resourceId ? ` · ${safeText(event.resourceId)}` : ''}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}
        </>
      )}
    </>
  );
}

export default function FederationSettings() {
  return (
    <FederationGate>
      <SettingsInner />
    </FederationGate>
  );
}
